import { createContext, useContext } from "react";

/**
 * Display or edit mode of the surrounding object page (UX6). `null` outside an object page: dialogs, list pages and
 * setup pages keep showing every field.
 */
export type ObjectPageMode = "display" | "edit";

const ObjectPageModeContext = createContext<ObjectPageMode | null>(null);

export const ObjectPageModeProvider = ObjectPageModeContext.Provider;

/** The mode of the object page this component is rendered in, or `null` outside an object page. */
export function useObjectPageMode(): ObjectPageMode | null {
  return useContext(ObjectPageModeContext);
}

/**
 * Whether a field value counts as "no value" (UX6): nothing, blank text or a lone dash. `false` (a boolean shown as
 * "No") and `0` are values and are never empty.
 */
export function isEmptyFieldValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") {
    const text = value.trim();
    return text === "" || text === "—" || text === "-";
  }
  return false;
}

/**
 * True when a read-only field must be hidden (UX6, Fiori object page guideline): the page is in display mode and the
 * field has no value. In edit mode, and outside object pages, every field stays visible.
 */
export function useHidesEmptyField(editing: boolean, value: unknown): boolean {
  const mode = useObjectPageMode();
  return !editing && mode === "display" && isEmptyFieldValue(value);
}
